import type { FC } from 'react';
import { useEffect, useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import type {
  BeneficialOwnerData,
  BeneficialOwnerResponse,
  DocumentUpload,
  IANATimezones
} from '@gcv/shared';
import { BoCountriesSelectOptions, BoStateSelectOptions } from '@gcv/shared';
import { Box } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';

import { DocumentsApi } from 'api';
import { isRequiredMessage, mustBeBetween0And100Message } from 'domain/consts/form-rule-messages';
import { SsnMask } from 'domain/consts/input-masks';
import { environment } from 'environments/environment';
import { DOCUMENTS_KEY } from 'hooks/keys';
import { useDocuments } from 'hooks/queries';
import { getSnackbarStore } from 'stores/SnackBarStore';
import { getUserStore } from 'stores/UserStore';
import {
  Button,
  CheckBox,
  Dialog,
  FileContainer,
  Form,
  InputDate,
  InputMasked,
  InputNumber,
  InputRadio,
  InputSelect,
  InputText,
  InputZip,
  Label,
  Spinner,
  Text
} from 'ui';
import palette from 'ui/theme/palette';
import { uploadOrgDocumentToS3 } from 'util/s3.util';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: BeneficialOwnerData) => void;
  isSaving: boolean;
  crbId: string;
  timezone: IANATimezones;
  addOrEdit: 'add' | 'edit';
  owner?: BeneficialOwnerResponse;
}

export const BeneficialOwnerModalForm: FC<Props> = (props) => {
  const { isOpen, onClose, onSave, isSaving, crbId, timezone, addOrEdit, owner } = props;
  const [isUploadingDocuments, setIsUploadingDocuments] = useState(false);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [filesToDelete, setFilesToDelete] = useState<DocumentUpload[]>([]);
  const [hasAttested, setHasAttested] = useState(false);

  const user = getUserStore().user;
  const queryClient = useQueryClient();
  const { documentsMap, isErrorDocuments, isLoadingDocuments } = useDocuments(isOpen ? crbId : '');

  const documentsApi = new DocumentsApi();
  const documents = (owner?.identification_doument_ids ?? [])
    .map((documentId) => documentsMap[documentId])
    .filter(Boolean);

  const getDefaultValues = (): FieldValues => {
    return {
      beneficial_owner_first_name: owner?.beneficial_owner_first_name ?? '',
      middle_initial: owner?.middle_initial ?? '',
      beneficial_owner_last_name: owner?.beneficial_owner_last_name ?? '',
      job_title_occupation: owner?.job_title_occupation ?? '',
      date_of_birth: owner?.date_of_birth ?? '',
      street_address: owner?.street_address ?? '',
      city: owner?.city ?? '',
      state: owner?.state ?? '',
      zip_code: owner?.zip_code ?? '',
      country: owner?.country ?? '',
      us_person: owner?.us_person ?? true,
      identification_id_us_person: owner?.us_person !== false ? owner?.identification_id ?? '' : '',
      identification_id_non_us_person: owner?.us_person === false ? owner?.identification_id ?? '' : '',
      country_of_issuance: owner?.country_of_issuance ?? '',
      identification_doument_ids: owner?.identification_doument_ids ?? [],
      ownership_in_this_business: owner?.ownership_in_this_business ?? 0
    };
  };

  const attestingName = [user.firstName, user.lastName].filter(Boolean).join(' ');

  const form = useForm<FieldValues>({ mode: 'onBlur', defaultValues: getDefaultValues() });

  useEffect(() => {
    if (!isOpen) return;
    form.reset(getDefaultValues());
    setNewFiles([]);
    setFilesToDelete([]);
    setHasAttested(false);
  }, [attestingName, form.reset, isOpen, owner]);

  useEffect(() => {
    if (!form.formState.isValid) setHasAttested(false);
  }, [form.formState.isValid]);

  useEffect(() => {
    if (isErrorDocuments) {
      getSnackbarStore().showErrorSnackbarMessage('There was an issue getting attached documents.');
    }
  }, [isErrorDocuments]);

  const handleSubmit = (data: FieldValues) => {
    setIsUploadingDocuments(true);
    return Promise.all(
      newFiles.map(async (file) => {
        const s3Key = await uploadOrgDocumentToS3(file, crbId);
        return documentsApi.putDocument(file.name, s3Key, crbId);
      })
    )
      .then((docUploads) => {
        if (docUploads.length) {
          const documentsQueryKey = [DOCUMENTS_KEY, crbId];
          const cachedDocuments = queryClient.getQueryData<DocumentUpload[]>(documentsQueryKey);

          if (cachedDocuments) {
            queryClient.setQueryData<DocumentUpload[]>(documentsQueryKey, [
              ...cachedDocuments.filter(
                (cachedDocument) => !docUploads.some((docUpload) => docUpload.id === cachedDocument.id)
              ),
              ...docUploads
            ]);
          } else {
            queryClient.invalidateQueries({ queryKey: documentsQueryKey });
          }
        }

        return saveOwner(
          data,
          docUploads.map((d) => d.id)
        );
      })
      .catch(() => {
        getSnackbarStore().showErrorSnackbarMessage('There was an issue uploading documents.');
      })
      .finally(() => {
        setIsUploadingDocuments(false);
      });
  };

  const saveOwner = (data: FieldValues, newDocIds: string[]) => {
    const beneficialOwner: BeneficialOwnerData = {
      beneficial_owner_first_name: data.beneficial_owner_first_name,
      middle_initial: data.middle_initial || undefined,
      beneficial_owner_last_name: data.beneficial_owner_last_name,
      job_title_occupation: data.job_title_occupation,
      date_of_birth: DateTime.fromISO(data.date_of_birth, { zone: timezone }).toFormat('yyyy-MM-dd'),
      street_address: data.street_address,
      city: data.city,
      state: data.state,
      zip_code: data.zip_code,
      country: data.country,
      us_person: data.us_person,
      identification_id: data.us_person
        ? data.identification_id_us_person.replace(/-/g, '')
        : data.identification_id_non_us_person,
      country_of_issuance: data.us_person ? undefined : data.country_of_issuance,
      identification_doument_ids: [...data.identification_doument_ids, ...newDocIds].filter(
        (docId) => !filesToDelete.some((docUpload) => docUpload.id === docId)
      ),
      ownership_in_this_business: data.ownership_in_this_business
    };
    onSave(beneficialOwner);
  };

  return (
    <Dialog
      isOpen={isOpen}
      handleClose={onClose}
      title={addOrEdit === 'edit' ? 'Edit Beneficial Owner' : 'Add Beneficial Owner'}
      action={
        <Button
          color="primary"
          label={addOrEdit === 'edit' ? 'Save Changes' : 'Add Owner'}
          onClick={form.handleSubmit(handleSubmit)}
          disabled={isUploadingDocuments || isSaving || !form.formState.isValid || !hasAttested}
          isLoading={isUploadingDocuments || isSaving}
        />
      }
      width="600px"
      dataCy="beneficial-owner-modal"
    >
      <Form>
        <Box sx={{ display: 'flex', flexDirection: 'column', p: 0, width: '100%' }}>
          <Box sx={{ display: 'flex', p: 0, width: '100%' }}>
            <Box sx={{ width: '40%', pt: 1, pr: 1, pb: 1, pl: 0 }}>
              <InputText
                {...form}
                name="beneficial_owner_first_name"
                label="First Name"
                rules={{ required: { value: true, message: 'is required' } }}
              />
            </Box>
            <Box sx={{ width: '15%', p: 1 }}>
              <InputText {...form} name="middle_initial" label="M.I." />
            </Box>
            <Box sx={{ width: '45%', pt: 1, pr: 0, pb: 1, pl: 1 }}>
              <InputText
                {...form}
                name="beneficial_owner_last_name"
                label="Last Name"
                rules={{ required: { value: true, message: 'is required' } }}
              />
            </Box>
          </Box>

          <Box sx={{ width: '100%', py: 1, px: 0 }}>
            <InputText
              {...form}
              name="job_title_occupation"
              label="Job Title / Occupation"
              rules={{ required: { value: true, message: 'is required' } }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, width: '100%', py: 1 }}>
            <Box sx={{ flex: 1 }}>
              <InputDate
                {...form}
                name="date_of_birth"
                label="Date of Birth"
                timezone={timezone}
                disableFutureDates
                rules={{ required: { value: true, message: 'is required' } }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <InputNumber
                {...form}
                name="ownership_in_this_business"
                label="% Ownership"
                rules={{
                  required: { value: true, message: isRequiredMessage },
                  min: { value: 0, message: mustBeBetween0And100Message },
                  max: { value: 100, message: mustBeBetween0And100Message }
                }}
              />
            </Box>
          </Box>

          <Box sx={{ width: '100%', py: 1, px: 0 }}>
            <InputText
              {...form}
              name="street_address"
              label="Street Address"
              rules={{ required: { value: true, message: 'is required' } }}
            />
          </Box>

          <Box sx={{ display: 'flex', p: 0, width: '100%' }}>
            <Box sx={{ width: '45%', pt: 1, pr: 1, pb: 1, pl: 0 }}>
              <InputText
                {...form}
                name="city"
                label="City"
                rules={{ required: { value: true, message: 'is required' } }}
              />
            </Box>
            <Box sx={{ width: '30%', p: 1 }}>
              <InputSelect
                {...form}
                name="state"
                label="State"
                options={BoStateSelectOptions.map((x) => ({ label: x, value: x }))}
                rules={{ required: { value: true, message: 'is required' } }}
              />
            </Box>
            <Box sx={{ width: '25%', pt: 1, pr: 0, pb: 1, pl: 1 }}>
              <InputZip
                {...form}
                name="zip_code"
                label="ZIP"
                rules={{ required: { value: true, message: 'is required' } }}
              />
            </Box>
          </Box>

          <Box sx={{ width: '60%', py: 1, px: 0 }}>
            <InputSelect
              {...form}
              name="country"
              label="Country"
              options={BoCountriesSelectOptions.map((x) => ({ label: x, value: x }))}
              rules={{ required: { value: true, message: 'is required' } }}
            />
          </Box>

          <Box sx={{ width: '100%', pt: 2, pb: 1, px: 0 }}>
            <InputRadio
              {...form}
              name="us_person"
              label="US Person or Non-US Person"
              justifyContent="row"
              onChange={() =>
                form.clearErrors(['identification_id_us_person', 'identification_id_non_us_person'])
              }
              rules={{ validate: (value) => value !== undefined || 'is required' }}
              options={[
                { label: 'US Person', value: true },
                { label: 'Non-US Person', value: false }
              ]}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, width: '100%', py: 1 }}>
            {form.watch('us_person') === true && (
              <Box sx={{ flex: 1 }}>
                <InputMasked
                  {...form}
                  name="identification_id_us_person"
                  label="US Person - SSN"
                  rules={{
                    required: { value: true, message: 'is required' },
                    minLength: { message: 'must be in ###-##-#### format', value: 11 },
                    maxLength: { message: 'must be in ###-##-#### format', value: 11 }
                  }}
                  mask={SsnMask}
                />
              </Box>
            )}
            {form.watch('us_person') === false && (
              <>
                <Box sx={{ flex: 1 }}>
                  <InputText
                    {...form}
                    name="identification_id_non_us_person"
                    label="Non US-Person - SSN, ITIN, Passport, etc."
                    rules={{ required: { value: true, message: 'is required' } }}
                  />
                </Box>

                <Box sx={{ flex: 1 }}>
                  <InputSelect
                    {...form}
                    name="country_of_issuance"
                    label="Country of Issuance"
                    options={BoCountriesSelectOptions.map((x) => ({ label: x, value: x }))}
                    rules={{ required: { value: true, message: 'is required' } }}
                  />
                </Box>
              </>
            )}
          </Box>

          {isLoadingDocuments ? (
            <Spinner />
          ) : (
            <Box sx={{ width: '100%', py: 1, px: 0 }}>
              <FileContainer
                fullWidth
                multiple
                acceptedTypes="imagesAndPdfs"
                existingFilesLabel="ID Documentation"
                existingS3Files={documents || []}
                fileBucket={environment.storageConfig.orgDocument}
                uploadFilesLabel="ID Documentation"
                onNewFilesChange={setNewFiles}
                onRemoveExistingFile={(docUpload) => setFilesToDelete((prev) => [...prev, docUpload])}
                dataCy="beneficial-owner-documentation"
              />
            </Box>
          )}

          <Box sx={{ borderTop: '1px solid', borderColor: 'divider', mt: 2, pt: 2 }}>
            <Label content="Attestation" />
            <Text
              content={
                "An authorized individual must attest this owner's information is accurate. " +
                'This is a soft attestation — the owner is certified only when included in ' +
                'a submitted questionnaire.'
              }
              type="body3"
              sx={{ pt: 1 }}
            />
            <Box sx={{ display: 'flex', pt: 2, px: 0, width: '100%' }}>
              <Box sx={{ width: '50%', pt: 1, pr: 1, pb: 1, pl: 0 }}>
                <Label content="Attesting Individual" />
                <Text content={attestingName} type="body1" sx={{ pt: 0.5 }} />
              </Box>
            </Box>
            <Box
              sx={{
                alignItems: 'flex-start',
                background: palette.warning.background,
                border: `1px solid ${palette.warning.border}`,
                borderRadius: 1,
                display: 'flex',
                mt: 1,
                p: 1.5
              }}
            >
              <CheckBox
                labelPlacement="end"
                name="beneficial-owner-attestation"
                onChange={(_, checked) => setHasAttested(checked)}
                readOnly={!form.formState.isValid}
                value={hasAttested}
              />
              <Box sx={{ color: palette.warning.text, lineHeight: 1.5, pl: 1, pt: '1px' }}>
                <Text
                  content="I confirm this is accurate to the best of my knowledge."
                  type="body3"
                  sx={{ fontWeight: 700 }}
                />
                <Text
                  content="Your name and timestamp will be recorded with that attestation."
                  type="body3"
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Form>
    </Dialog>
  );
};
